import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { Alert } from '@/components/ui/alert';
import { InlineSpinner } from '@/components/ui/loading-spinner';
import { Eye, EyeOff } from 'lucide-react';
import authService from '../services/authService';

// Admin Portal Logo Component
interface AdminLogoProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showText?: boolean;
  className?: string;
}

const AdminLogo = ({ size = 'md', showText = true, className = '' }: AdminLogoProps) => {
  const sizeClasses: Record<string, string> = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16'
  };

  const textSizeClasses: Record<string, string> = {
    sm: 'text-sm',
    md: 'text-lg',
    lg: 'text-xl',
    xl: 'text-2xl'
  };

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      {/* Admin Dashboard Icon */}
      <div className={`${sizeClasses[size]} flex-shrink-0`}>
        <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
          {/* Gradient definitions */}
          <defs>
            <linearGradient id="adminGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{stopColor:'#4F46E5', stopOpacity:1}} />
              <stop offset="100%" style={{stopColor:'#7C3AED', stopOpacity:1}} />
            </linearGradient>
            <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="1" stdDeviation="1" floodOpacity="0.2"/>
            </filter>
          </defs>

          {/* Modern admin dashboard icon */}
          <g transform="translate(6, 6)">
            {/* Main panel */}
            <rect x="0" y="0" width="36" height="36" rx="6" fill="url(#adminGrad)" filter="url(#shadow)"/>

            {/* Top bar */}
            <rect x="4" y="4" width="28" height="6" rx="2" fill="white" opacity="0.9"/>

            {/* Left panel */}
            <rect x="4" y="14" width="12" height="18" rx="2" fill="white" opacity="0.8"/>

            {/* Right panels */}
            <rect x="20" y="14" width="12" height="8" rx="2" fill="white" opacity="0.7"/>
            <rect x="20" y="24" width="12" height="8" rx="2" fill="white" opacity="0.9"/>

            {/* Small decorative elements */}
            <circle cx="8" cy="7" r="1" fill="#4F46E5"/>
            <circle cx="12" cy="7" r="1" fill="#4F46E5"/>
            <circle cx="16" cy="7" r="1" fill="#4F46E5"/>
          </g>
        </svg>
      </div>

      {/* Text */}
      {showText && (
        <div className="flex flex-col">
          <span className={`font-bold text-indigo-600 leading-tight ${textSizeClasses[size]}`}>
            BayesPrep
          </span>
          <span className={`text-xs text-indigo-500 font-medium leading-tight ${size === 'sm' ? 'text-xs' : 'text-sm'}`}>
            Admin Portal
          </span>
        </div>
      )}
    </div>
  );
};

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [client, setClient] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { login, isAuthenticated, user } = useAuth();
  const { toast } = useToast();
  const { handleError, clearError, isError, error: errorInfo } = useErrorHandler({
    showToast: false, // We'll handle toast manually for better control
    scrollToError: true,
    autoFocus: true
  });
  const navigate = useNavigate();



  // If already authenticated, redirect to dashboard
  useEffect(() => {
    console.log('LoginPage - Auth check:', { isAuthenticated, user: !!user });

    if (isAuthenticated && user) {
      console.log('LoginPage - Already authenticated, redirecting to dashboard');
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('LoginPage - Form submitted');
    setIsLoading(true);
    setError('');

    // Validate client field
    if (!client.trim()) {
      setError('Client field is required');
      setIsLoading(false);
      return;
    }

    try {
      console.log('LoginPage - Attempting login with:', email, 'for client:', client);

      // Set tenant context BEFORE making the login request
      const tenantCode = client.trim().toUpperCase();
      localStorage.setItem('tenantCode', tenantCode);

      // Use authService with tenant validation
      const result = await authService.login(email, password, tenantCode);
      console.log('LoginPage - Login API response:', result);

      // Update AuthContext with processed user data
      login({
        id: result.userId,
        username: result.username || result.email,
        email: result.email,
        name: result.name,
        role: result.roles?.includes('ROLE_ADMIN') ? 'ADMIN' : 'USER',
        collegeId: result.tenantId || client.trim().toUpperCase()
      }, result.isFirstTimeLogin);

      console.log('LoginPage - Login successful, auth context updated');

      toast({
        title: 'Login Successful',
        description: 'Welcome to the BayesPrep Admin Portal',
      });

      // Check if this is a first-time login and navigate accordingly
      if (result.isFirstTimeLogin) {
        console.log('LoginPage - First-time login detected, redirecting to password reset');
        navigate('/first-time-password-reset', { replace: true });
      } else {
        console.log('LoginPage - Regular login, redirecting to dashboard');
        navigate('/', { replace: true });
      }
    } catch (error) {
      console.error('LoginPage - Login failed:', error);
      const errorInfo = handleError(error);
      setError(errorInfo.message);

      toast({
        title: errorInfo.title,
        description: errorInfo.message,
        variant: 'destructive',
        scrollToView: true
      });
    }

    setIsLoading(false);
  };

  // Clear error when user starts typing
  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (error) {
      setError('');
      clearError();
    }
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    if (error) {
      setError('');
      clearError();
    }
  };

  const handleClientChange = (value: string) => {
    setClient(value);
    if (error) {
      setError('');
      clearError();
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900 px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <AdminLogo size="xl" showText={true} />
          </div>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Sign in to your administrator account
          </p>
        </div>

        <div className="mt-8 bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <Label htmlFor="client" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Institution Code
              </Label>
              <Input
                id="client"
                name="client"
                type="text"
                autoComplete="organization"
                required
                value={client}
                onChange={(e) => handleClientChange(e.target.value)}
                placeholder="Enter institution code"
                className="mt-1"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Email Address
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => handleEmailChange(e.target.value)}
                placeholder="Enter your email address"
                className="mt-1"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Password
                </Label>
                <Link
                  to="/forgot-password"
                  className="text-sm font-medium text-primary hover:text-primary/90"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => handlePasswordChange(e.target.value)}
                  placeholder="Enter your password"
                  className="mt-1 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {(error || isError) && (
              <Alert
                variant="destructive"
                title={errorInfo?.title || "Login Error"}
                description={error || errorInfo?.message}
                scrollToView={true}
                autoFocus={true}
                id="login-error"
              />
            )}

            <div>
              <Button
                type="submit"
                className="w-full py-3 text-base disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <InlineSpinner size="sm" className="mr-2" />
                    Signing in...
                  </div>
                ) : (
                  'Sign in'
                )}
              </Button>
            </div>
          </form>


        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            BayesPrep Admin Portal v1.0
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
